import { execSync } from "node:child_process";
import { getCurrentBranch, hasUncommittedChanges } from "./git-utils.js";

/**
 * Handler for `session:pullBranch` events. Fired against a parent task's
 * connected session when a child PR merges into the parent's branch (feature
 * branch packs). Performs a fast-forward `git pull` of the parent's own
 * branch so the workspace and preview reflect merged child work.
 *
 * No-op (with a warning) if the workspace is dirty or on a different branch —
 * we never want to surprise the agent or the user by overwriting in-flight work.
 */
export function handlePullBranch(workDir: string, branch: string): void {
  if (!branch) return;

  const current = getCurrentBranch(workDir);
  if (current !== branch) {
    process.stderr.write(
      `[conveyor-agent] pull_branch ignored — current branch ${current ?? "(detached)"} != ${branch}\n`,
    );
    return;
  }

  if (hasUncommittedChanges(workDir)) {
    process.stderr.write(
      `[conveyor-agent] pull_branch ignored — uncommitted changes on ${branch}\n`,
    );
    return;
  }

  try {
    execSync(`git fetch origin ${branch}`, { cwd: workDir, stdio: "ignore", timeout: 60_000 });
  } catch {
    process.stderr.write(`[conveyor-agent] pull_branch: fetch failed for ${branch}\n`);
    return;
  }

  try {
    execSync(`git pull --ff-only origin ${branch}`, {
      cwd: workDir,
      stdio: "ignore",
      timeout: 60_000,
    });
    process.stderr.write(`[conveyor-agent] pull_branch: pulled origin/${branch}\n`);
  } catch {
    process.stderr.write(
      `[conveyor-agent] pull_branch: ff-only pull failed for ${branch} (likely diverged)\n`,
    );
  }
}
